import React from 'react';
import useConstant from 'use-constant';
import awesomeDebouncePromise from 'awesome-debounce-promise';
import {useAsync} from 'react-async';

// 🐨 put the below line at the entry point of webpack for shared folder
// import * as styledcomponenttypes from 'styled-components/cssprop';

// COMPONENTS and HOOKS
import Text from '../../../../../core-components/TextFieldExpt/TextFieldExpt';
import useAutosuggest from '../../../../../../shared/hooks/useAutosuggest';

// STYLES
import * as styles from './SearchWidgetAutosuggest.styles';

import * as S from '../HomePageAutosuggest/HomePageAutosuggest.styles';

// UTILS and CONSTANTS
import {itemToString} from '../utils';

// APP CONFIG
import {searchWidgetLocationSearchURL} from '../../../../../../shared/config/app-config';

import {DEFAULT_ERROR_MESSAGE, DEBOUNCE_TIMEOUT} from '../constants';
import AutosuggestIconSelector from '../../../../../../shared/components/AutosuggestIconSelector';
import TripAdvisorIcon from '../../../../../Icons/TripAdvisorIcon';
import {getPlacesId} from '../HomePageAutosuggest/utils';

const fetchVoyagerLocationSuggestions = async ({inputValue}, {signal}) => {
  if (!inputValue) {
    return [];
  }

  const fetchVoyagerUrlForInput = searchWidgetLocationSearchURL(inputValue);
  const res = await fetch(fetchVoyagerUrlForInput, {signal});
  if (!res.ok) throw new Error(res.statusText);
  const ret = await res.json();

  if (ret.data && ret.data.r) {
    return ret.data.r;
  }

  /** 🐨 handle gracefully if voyager doesn't return ret.data.r an array */
};

function SearchWidgetAutosuggest({initialInputValue, onItemSelection}) {
  const {
    propGetters: {getInputProps, getMenuProps, getItemProps, getRootProps, getLabelProps},
    state: {isOpen, highlightedIndex, selectedItem, inputValue},
  } = useAutosuggest({
    itemToString,
    initialInputValue,
    async onSelectedItemChange(changes) {
      let updatedSuggestion = changes.selectedItem || {};

      if (!changes.selectedItem._id) {
        try {
          updatedSuggestion = await getPlacesId(changes.selectedItem);
        } catch (err) {
          console.trace(err);
          // opportunity for toast message
        }
      }
      onItemSelection({...changes, selectedItem: updatedSuggestion});
    },
  });

  const debouncedVoyagerFetchPromise = useConstant(() =>
    awesomeDebouncePromise(fetchVoyagerLocationSuggestions, DEBOUNCE_TIMEOUT),
  );

  // @ts-ignore
  const {data: items = [], isPending, isRejected, error} = useAsync({
    promiseFn: debouncedVoyagerFetchPromise,
    watch: inputValue,
    inputValue,
  });

  return (
    // @ts-ignore
    <styles.AutocompleteWrapperStyles {...getRootProps()}>
      <styles.FieldWrapper>
        <styles.SearchLabelStyles {...getLabelProps()}>
          AREA, LANDMARK OR HOTEL NAME
        </styles.SearchLabelStyles>
        <styles.SearchInputStyles
          {...getInputProps({
            // @ts-ignore
            placeholder: 'LOCATION NAME',
            // value: (value && value.n) || 'LOCATION NAME',
          })}
        />
      </styles.FieldWrapper>

      {isRejected && (error || DEFAULT_ERROR_MESSAGE) && (
        <S.ErrorMessage>
          <Text align="left" bold>
            {error}
          </Text>
        </S.ErrorMessage>
      )}

      <S.SearchMenuStyles top="5rem" left="0" {...getMenuProps()}>
        {isOpen
          ? items.map((item, index) => {
              const key = `${item._id}-${item.n ? item.n : ''}` || item.n || index;

              return (
                <S.SearchListItem
                  // @ts-ignore
                  {...getItemProps({
                    item,
                    index,
                    isActive: highlightedIndex === index,
                    isSelected: selectedItem === item,
                  })}
                  /** 🐨tell voyager about below */
                  /** 🐨take 'identifier' from the user of the Autosuggest component
                   * this shouldn't be hardcoded
                   */
                  key={key} // fallback because voyager sends _id = null sometimes
                >
                  <S.StyledIcon>
                    <AutosuggestIconSelector name={item.t} />
                  </S.StyledIcon>
                  <S.SearchListItemLocationName>{itemToString(item)}</S.SearchListItemLocationName>
                  <S.SearchListItemLocationType>
                    <div>{item.t || null}</div>
                    {item.xtr && item.xtr.g_rtg && (
                      <S.RatingStyles rating={item.xtr.g_rtg}>
                        {/* gri === 3 is triadvisor */}
                        {item.xtr && item.xtr.gri === 3 && (
                          // 🐨 use <Image /> component when its ready
                          <TripAdvisorIcon />
                        )}
                        <Text inline customFontSize="1.4">
                          {item.xtr.g_rtg} / 5
                        </Text>
                      </S.RatingStyles>
                    )}
                    {(item.nh && item.nh > 0 && (
                      <S.SearchHotelNumber>{item.nh} Hotels</S.SearchHotelNumber>
                    )) ||
                      null}
                  </S.SearchListItemLocationType>
                </S.SearchListItem>
              );
            })
          : null}
      </S.SearchMenuStyles>
    </styles.AutocompleteWrapperStyles>
  );
}

export default SearchWidgetAutosuggest;
